import { REFETCH_INTERVAL_FAST } from '../confs/app';
import { hashFn } from './hash-fn';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import localForage from 'localforage';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: hashFn,
      gcTime: 10 * 60 * 1e3,
      staleTime: 30 * 1e3, // 30 seconds - keep cached data fresh longer to avoid flicker on refresh
      retry: 1,
      retryOnMount: true,
      refetchInterval: false, // 1 * 60 * 1e3,
      refetchOnReconnect: true,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
    },
    mutations: {
      gcTime: 10 * 60 * 1e3,
    },
  },
});

// clear localforage history version
// typeof guard: a bare `indexedDB` reference throws ReferenceError where the
// global is absent entirely (jsdom tests, some webviews) instead of being falsy.
if (typeof indexedDB !== 'undefined' && indexedDB) {
  indexedDB.deleteDatabase('query-0.1');
  indexedDB.deleteDatabase('query-0.6');
  indexedDB.deleteDatabase('query-0.2'); // Clear old cache to pick up historical market schema changes
  indexedDB.deleteDatabase('query-0.3'); // Clear old cache to pick up updated market images
  indexedDB.deleteDatabase('query-0.4'); // Clear old cache to pick up banner changes
  indexedDB.deleteDatabase('query-0.5'); // Poisoned: held JSON husks of wagmi clients (functions stripped) → black-screen crash
}

export const storage = localForage.createInstance({ name: 'query-0.7' }); // 0.6 is in the delete list above — skip it

export const persister = createAsyncStoragePersister({ storage });

// Identity-bearing queries must NEVER be persisted to IndexedDB: a revived
// UserLogin from a previous user/session leaks the old uid into a new session
// (and revived isNewUser retriggers login side effects).
const IDENTITY_QUERY_KEYS = ['UserLogin', 'Me', 'SignedUserInfo', 'SignedUserAssets', 'UserPositions'];

export const persistOptions = {
  persister,
  dehydrateOptions: {
    // Persist ONLY app SDK content queries, whose keys look like
    // [queryKey-option (undefined), 'Name', params]. wagmi shares this
    // queryClient and its query data holds viem clients — JSON-serializing
    // those strips the functions, and the rehydrated husk crashes
    // createWalletClient (undefined .request → black screen). Never persist
    // anything whose key doesn't match the app shape.
    shouldDehydrateQuery: (query: { state: { status: string }; queryKey: readonly unknown[] }) =>
      query.state.status === 'success' &&
      query.queryKey[0] == null &&
      typeof query.queryKey[1] === 'string' &&
      !IDENTITY_QUERY_KEYS.includes(query.queryKey[1]),
  },
};
